/* Native Shopify Pages are the source of truth. No Admin token or app is needed. */
(() => {
  'use strict';

  const normalize = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  const plainText = (value) => {
    // Liquid strip_html removes tags, but leaves entities from the rich editor.
    // Decode in an inert textarea, then insert only through textContent below.
    const decoder = document.createElement('textarea');
    decoder.innerHTML = String(value || '');
    return decoder.value;
  };
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const localURL = (path) => {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Guide links must remain on this store.');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };

  async function directory(root) {
    if (root.dataset.guideReady) return;
    root.dataset.guideReady = 'true';
    const results = root.querySelector('[data-guide-results]');
    const status = root.querySelector('[data-guide-load-status]');
    const search = root.querySelector('[data-guide-search]');
    const count = root.querySelector('[data-guide-count]');
    const more = root.querySelector('[data-guide-more]');
    const categoryHost = root.querySelector('[data-guide-categories]');
    const categoryFilter = root.querySelector('[data-guide-category-filter]');
    categoryFilter.open = window.matchMedia('(min-width: 750px)').matches;
    const parameters = new URLSearchParams(window.location.search);
    const state = { query: parameters.get('q') || '', category: parameters.get('category') || '', limit: 40 };
    const endpoint = localURL(root.dataset.guideUrl);
    endpoint.searchParams.set('view', 'metalwood-guide-data');
    status.hidden = false;
    status.textContent = 'Loading the full guide for search and category browsing…';

    async function batch(number) {
      const url = new URL(endpoint);
      url.searchParams.set('page', number);
      // This is a Liquid alternate view containing JSON. Asking Shopify for
      // application/json bypasses the view and returns its native Page API.
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Guide index request failed.');
      const data = await response.json();
      if (data.schema !== 1 || !Array.isArray(data.entries) || data.page !== number || !Number.isInteger(data.pages) || data.pages < 1) {
        throw new Error('Guide index response was incomplete.');
      }
      // Shopify cannot paginate beyond its first 25,000 store pages. Do not
      // misrepresent a truncated index as a complete guide if that ceiling is hit.
      if (data.total_store_pages >= 25000 || data.pages > 500) throw new Error('Shopify pagination ceiling reached.');
      return data;
    }

    try {
      const first = await batch(1);
      const batches = [first];
      // Three small requests at once keep the guide responsive while respecting
      // the theme endpoint. Every page is fetched, including batches without entries.
      for (let start = 2; start <= first.pages; start += 3) {
        const numbers = Array.from({ length: Math.min(3, first.pages - start + 1) }, (_, index) => start + index);
        batches.push(...await Promise.all(numbers.map(batch)));
      }
      if (batches.some((item) => item.pages !== first.pages || item.total_store_pages !== first.total_store_pages)) {
        throw new Error('Pages changed during index loading. Reload to retry.');
      }
      const allEntries = [...new Map(batches.flatMap((item) => item.entries).map((entry) => [entry.id, entry])).values()];
      const entries = allEntries.filter((entry) => !entry.redirect_to && entry.namespace !== 14);
      const collator = new Intl.Collator(document.documentElement.lang || 'en', { sensitivity: 'base', numeric: true });
      entries.sort((a, b) => collator.compare(a.title, b.title));
      entries.forEach((entry) => {
        entry.categories = Array.isArray(entry.categories) ? entry.categories : [];
        entry.text = plainText(entry.text);
        const aliases = allEntries.filter((other) => other.redirect_to === entry.url).map((other) => other.title);
        entry.searchNames = [entry.title, ...aliases].map(normalize);
        entry.searchText = normalize([entry.title, ...aliases, ...entry.categories, entry.text].join(' '));
        entry.url = localURL(entry.url).href;
      });
      const categories = [...new Set(entries.flatMap((entry) => entry.categories))].sort(collator.compare);

      function updateURL() {
        const url = new URL(window.location.href);
        state.query ? url.searchParams.set('q', state.query) : url.searchParams.delete('q');
        state.category ? url.searchParams.set('category', state.category) : url.searchParams.delete('category');
        url.searchParams.delete('page');
        window.history.replaceState(null, '', url);
      }
      function render() {
        const phrase = normalize(state.query).trim();
        const terms = phrase.split(/\s+/).filter(Boolean);
        const found = entries.filter((entry) => (!state.category || entry.categories.includes(state.category)) && terms.every((term) => entry.searchText.includes(term)));
        if (phrase) {
          const rank = (entry) => entry.searchNames.some((name) => name === phrase) ? 0
            : entry.searchNames.some((name) => name.startsWith(phrase)) ? 1
              : entry.searchNames.some((name) => name.includes(phrase)) ? 2 : 3;
          found.sort((a, b) => rank(a) - rank(b) || collator.compare(a.title, b.title));
        }
        root.querySelector('[data-guide-list-heading]').textContent = state.query ? 'Search results' : state.category || 'All entries';
        root.querySelector('[data-guide-category-label]').textContent = state.category ? `Category: ${state.category}` : 'Browse categories';
        count.textContent = `${found.length.toLocaleString()} ${found.length === 1 ? 'entry' : 'entries'}`;
        categoryHost.querySelectorAll('button').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.category === state.category)));
        const list = node('ul', 'mw-guide-entries');
        found.slice(0, state.limit).forEach((entry) => {
          const row = node('li', 'mw-guide-entry');
          const link = node('a');
          link.href = entry.url;
          if (entry.categories.length) link.append(node('span', 'mw-guide-entry-type', entry.categories.join(' · ')));
          const title = node('span', 'mw-guide-entry-title', entry.title);
          const arrow = node('span', '', '→');
          arrow.setAttribute('aria-hidden', 'true');
          title.append(arrow);
          link.append(title);
          const plain = String(entry.text || '').replace(/\s+/g, ' ').trim();
          const excerpt = plain.length > 180 ? `${plain.slice(0, 180).replace(/\s+\S*$/, '')}…` : plain;
          link.append(node('span', 'mw-guide-entry-summary', excerpt));
          row.append(link);
          list.append(row);
        });
        results.replaceChildren(found.length ? list : node('p', 'mw-guide-load-status', 'No entries match. Try another search or category.'));
        more.hidden = found.length <= state.limit;
        more.textContent = `Show more entries (${Math.min(found.length, state.limit)} of ${found.length})`;
      }
      ['', ...categories].forEach((category) => {
        const button = node('button', 'mw-guide-category', category || 'All entries');
        button.type = 'button';
        button.dataset.category = category;
        button.append(node('span', '', String(category ? entries.filter((entry) => entry.categories.includes(category)).length : entries.length)));
        button.addEventListener('click', () => { state.category = category; state.limit = 40; updateURL(); render(); });
        categoryHost.append(button);
      });
      search.value = state.query;
      search.addEventListener('input', () => { state.query = search.value; state.limit = 40; updateURL(); render(); });
      more.addEventListener('click', () => {
        const previousCount = results.querySelectorAll('.mw-guide-entry').length;
        state.limit += 40;
        render();
        // Focus the first newly revealed article instead of losing keyboard
        // position when the final Show more button disappears.
        results.querySelectorAll('.mw-guide-entry a')[previousCount]?.focus();
      });
      root.querySelector('[data-guide-search-wrap]').hidden = false;
      root.querySelector('[data-guide-sidebar]').hidden = false;
      status.hidden = true;
      render();
    } catch (error) {
      status.textContent = 'The full guide search could not load. You can still browse the entries below using the page links. Reload this page to try search again.';
      console.warn('Metalwood guide:', error.message);
    }
  }

  function article(root) {
    if (root.dataset.guideReady) return;
    root.dataset.guideReady = 'true';
    const reader = root.querySelector('[data-guide-reader]');
    const paper = root.querySelector('[data-guide-paper]');
    const size = root.querySelector('[data-guide-size]');
    let preference = {};
    try { preference = JSON.parse(localStorage.getItem('metalwood-guide-reading') || '{}'); } catch (_) { /* Private browsing may disable storage. */ }
    const apply = () => {
      reader.classList.toggle('is-paper', preference.paper === true);
      paper.setAttribute('aria-pressed', String(preference.paper === true));
      paper.textContent = preference.paper ? 'Midnight reading mode' : 'Paper reading mode';
      const readingSize = ['18', '20', '22'].includes(String(preference.size)) ? String(preference.size) : '18';
      size.value = readingSize;
      reader.style.setProperty('--mw-reading-size', `${readingSize}px`);
    };
    const save = () => { try { localStorage.setItem('metalwood-guide-reading', JSON.stringify(preference)); } catch (_) { /* Reading controls still work without storage. */ } };
    paper.addEventListener('click', () => { preference.paper = !preference.paper; apply(); save(); });
    size.addEventListener('change', () => { preference.size = size.value; apply(); save(); });
    apply();
    root.querySelector('[data-guide-reading-tools]').hidden = false;

    const content = root.querySelector('[data-guide-content]');
    const title = reader.querySelector(':scope > h1');
    const leading = content.firstElementChild;
    if (title && leading?.tagName === 'H1' && normalize(leading.textContent.trim()) === normalize(title.textContent.trim())) {
      // Keep the author's original heading and its anchors in the stored body.
      // Show it once, using the same title styling as newly written articles.
      title.hidden = true;
      leading.classList.add('mw-guide-body-title');
    }
    const toc = root.querySelector('[data-guide-toc]');
    toc.open = window.matchMedia('(min-width: 750px)').matches;
    const headings = content.querySelectorAll('h2, h3');
    headings.forEach((heading, index) => {
      let anchor = heading.id || heading.querySelector('[id]')?.id;
      if (!anchor) {
        const stem = `guide-section-${index + 1}`;
        let id = stem;
        let counter = 1;
        while (document.getElementById(id)) id = `${stem}-${counter++}`;
        heading.id = id;
        anchor = id;
      }
      const row = node('li');
      row.dataset.level = heading.tagName.slice(1);
      const link = node('a', '', heading.textContent);
      link.href = `#${encodeURIComponent(anchor)}`;
      row.append(link);
      toc.querySelector('ol').append(row);
    });
    toc.hidden = headings.length === 0;
  }

  function initialize(context = document) {
    context.querySelectorAll('[data-guide-directory]').forEach(directory);
    context.querySelectorAll('[data-guide-article]').forEach(article);
  }
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
